import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getComplaintById, updateComplaint } from "../../services/complaintService";

export default function AdminComplaintEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const initialFormData = {
    complainantName: "",
    email: "",
    mobile: "",
    victimGender: "",
    status: "",
    remarks: "",
    statusChangedDate: null,
  };
  const [formData, setFormData] = useState({
    ...initialFormData,
  });
  const [originalFormData, setOriginalFormData] = useState({ ...initialFormData });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const getStatusHighlight = (status) => {
    switch (status) {
      case "Rejected":
        return { bg: "#f8d7da", border: "#dc3545", text: "#842029", label: "High Priority" };
      case "Forwarded":
        return { bg: "#cff4fc", border: "#0dcaf0", text: "#055160", label: "Action Sent" };
      case "Completed/Solved":
        return { bg: "#d1e7dd", border: "#198754", text: "#0f5132", label: "Resolved" };
      case "In Progress":
        return { bg: "#fff3cd", border: "#ffc107", text: "#664d03", label: "Ongoing" };
      case "Received":
        return { bg: "#e2e3e5", border: "#6c757d", text: "#41464b", label: "New" };
      default:
        return { bg: "#f8f9fa", border: "#ced4da", text: "#495057", label: "Not Selected" };
    }
  };

  useEffect(() => {
    fetchComplaint();
  }, []);

  const fetchComplaint = async () => {
    setLoading(true);
    try {
      const data = await getComplaintById(id);
      const normalized = {
        complainantName: data.complainantName,
        email: data.email,
        mobile: data.mobile,
        victimGender: data.victimGender,
        status: data.status,
        remarks: data.remarks || data.reason || "",
        statusChangedDate:
          data.statusChangedDate || data.StatusChangedDate || null,
      };
      setFormData(normalized);
      setOriginalFormData(normalized);
    } catch (err) {
      console.error(err);
      setMessage({ type: "danger", text: "Failed to fetch complaint." });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessage({ type: "", text: "" });
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    const nextErrors = {};
    const isReasonRequired =
      formData.status === "Forwarded" || formData.status === "Rejected";

    if (!formData.complainantName?.trim()) nextErrors.complainantName = "Complainant name is required.";

    if (!formData.email?.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.mobile?.trim()) {
      nextErrors.mobile = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      nextErrors.mobile = "Mobile number must be exactly 10 digits.";
    }

    if (!formData.victimGender) nextErrors.victimGender = "Victim gender is required.";
    if (!formData.status) nextErrors.status = "Status is required.";
    if (isReasonRequired && !formData.remarks?.trim()) {
      nextErrors.remarks = "Reason is required for Forwarded or Rejected status.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleReset = () => {
    setFormData(originalFormData);
    setErrors({});
    setMessage({ type: "", text: "" });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!validate()) return;

    setSubmitting(true);
    try {
      const hasStatusChanged =
        (formData.status || "").trim() !== (originalFormData.status || "").trim();

      const payload = {
        ...formData,
        statusChangedDate: hasStatusChanged
          ? new Date().toISOString()
          : formData.statusChangedDate || originalFormData.statusChangedDate || null,
      };

      await updateComplaint(id, payload);
      setMessage({ type: "success", text: "Complaint updated successfully." });
      setTimeout(() => navigate("/admin/complaints"), 600);
    } catch (err) {
      console.error("Error updating complaint:", err);
      setMessage({ type: "danger", text: "Failed to update complaint." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <h2>Edit Complaint</h2>
        <div>Loading...</div>
      </div>
    );
  }

  const statusHighlight = getStatusHighlight(formData.status);

  return (
    <div className="container">
      <h2>Edit Complaint</h2>
      {message.text ? (
        <div className={`alert alert-${message.type}`} role="alert">
          {message.text}
        </div>
      ) : null}
      <form onSubmit={handleSave}>
        <div className="row">
          <div className="col-md-3 mb-3">
            <label className="form-label">Complainant Name</label>
            <input
              type="text"
              className={`form-control ${errors.complainantName ? "is-invalid" : ""}`}
              name="complainantName"
              value={formData.complainantName}
              onChange={handleChange}
              required
            />
            {errors.complainantName && <div className="invalid-feedback">{errors.complainantName}</div>}
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>
          
          <div className="col-md-3 mb-3">
            <label className="form-label">Mobile</label>
            <input
              type="text"
              className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
            {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label">Victim Gender</label>
            <select
              className={`form-select ${errors.victimGender ? "is-invalid" : ""}`}
              name="victimGender"
              value={formData.victimGender}
              onChange={handleChange}
              required
            >
              <option value="">- Select -</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {errors.victimGender && <div className="invalid-feedback">{errors.victimGender}</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Status</label>
            <div
              className="p-2 rounded"
              style={{
                backgroundColor: statusHighlight.bg,
                border: `2px solid ${statusHighlight.border}`,
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small style={{ color: statusHighlight.text, fontWeight: 700 }}>
                  Selected Status: {formData.status || "None"}
                </small>
                <span
                  className="badge"
                  style={{
                    backgroundColor: statusHighlight.border,
                    color: "#fff",
                  }}
                >
                  {statusHighlight.label}
                </span>
              </div>
              <select
                className={`form-select ${errors.status ? "is-invalid" : ""}`}
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">- Select -</option>
                <option value="Received">Received</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed/Solved">Completed/Solved</option>
                <option value="Forwarded">Forwarded</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            {errors.status && <div className="invalid-feedback">{errors.status}</div>}
          </div>
          <div className="col-md-4 mb-3" />

          {(formData.status === "Forwarded" || formData.status === "Rejected") && (
            <div className="col-12 mb-3">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: "#fff3cd",
                  border: "2px solid #ffc107",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0 fw-bold">Reason/Remarks</label>
                  <span className="badge bg-warning text-dark">Required</span>
                </div>
                <textarea
                  className={`form-control ${errors.remarks ? "is-invalid" : ""}`}
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Write the reason..."
                  required
                />
                {errors.remarks && <div className="invalid-feedback">{errors.remarks}</div>}
              </div>
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-success" disabled={submitting}>
            {submitting ? "Saving..." : "Save Changes"}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={submitting}>
            Reset
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/complaints")}
            disabled={submitting}
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
}
